import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BuildIcon from "@mui/icons-material/Build";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import useDelete from "../../../api/useDelete";
import { useAuth } from "../../../context";
import AmbulanceForm from "./AmbulanceForm";

const STATUS_COLOR = {
  available: "success",
  on_trip: "warning",
  maintenance: "error",
  dispatched: "info",
  returned: "secondary",
  inactive: "default",
};

const DashCard = ({ icon, label, value, color }) => (
  <Grid item xs={6} sm={3}>
    <Card elevation={3} sx={{ borderRadius: 2, borderTop: `4px solid ${color}` }}>
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
        <Box sx={{ color, fontSize: 40 }}>{icon}</Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

const AmbulancePage = () => {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const queryStr = [search ? `search=${search}` : "", statusFilter ? `status=${statusFilter}` : ""]
    .filter(Boolean)
    .join("&");

  const endpoint = queryStr ? `ambulances/?${queryStr}` : "ambulances/";

  const { data, isLoading, isError, refetch } = useFetchObjects(["ambulances", search, statusFilter], endpoint, token);
  const { data: dashboard, refetch: refetchDash } = useFetchObjects(["ambulances-dashboard"], "ambulances/dashboard/", token);
  const { handleDelete, DeleteMessageComponent, ConfirmDialog } = useDelete("ambulances", token);

  const rows = Array.isArray(data) ? data : [];

  const statusLabel = (status) =>
    ({
      available: t("ambulance.available"),
      on_trip: t("ambulance.onTrip"),
      maintenance: t("ambulance.underMaintenance"),
      dispatched: t("ambulance.dispatched"),
      returned: t("ambulance.returned"),
      inactive: t("ambulance.inactive"),
    }[status] || status);

  const handleOpenAdd = () => {
    setEditItem(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditItem(item);
    setFormOpen(true);
  };

  const handleClose = () => {
    setFormOpen(false);
    setEditItem(null);
    refetch();
    refetchDash();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Grid container spacing={2} mb={3}>
        <DashCard icon={<LocalShippingIcon fontSize="inherit" />} label={t("ambulance.totalAmbulances")} value={dashboard?.total ?? 0} color="#1976d2" />
        <DashCard icon={<CheckCircleIcon fontSize="inherit" />} label={t("ambulance.available")} value={dashboard?.available ?? 0} color="#2e7d32" />
        <DashCard icon={<DirectionsCarIcon fontSize="inherit" />} label={t("ambulance.onTrip")} value={dashboard?.on_trip ?? 0} color="#ed6c02" />
        <DashCard icon={<BuildIcon fontSize="inherit" />} label={t("ambulance.underMaintenance")} value={dashboard?.maintenance ?? 0} color="#d32f2f" />
      </Grid>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" color="primary" fontWeight={700}>
            {t("ambulance.dashboardTitle")}
          </Typography>
          <Button variant="contained" color="primary" startIcon={<AddCircleIcon />} onClick={handleOpenAdd}>
            {t("ambulance.addAmbulance")}
          </Button>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("ambulance.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label={t("ambulance.status")} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">{t("ambulance.all")}</MenuItem>
              <MenuItem value="available">{t("ambulance.available")}</MenuItem>
              <MenuItem value="on_trip">{t("ambulance.onTrip")}</MenuItem>
              <MenuItem value="maintenance">{t("ambulance.underMaintenance")}</MenuItem>
              <MenuItem value="dispatched">{t("ambulance.dispatched")}</MenuItem>
              <MenuItem value="returned">{t("ambulance.returned")}</MenuItem>
              <MenuItem value="inactive">{t("ambulance.inactive")}</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f2f2f2" }}>
                {[t("ambulance.hash"), t("ambulance.code"), t("ambulance.plateNo"), t("ambulance.model"), t("ambulance.assignedDriver"), t("ambulance.driverPhone"), t("ambulance.location"), t("ambulance.status"), t("ambulance.actions")].map((h) => (
                  <TableCell key={h} align="center" sx={{ fontWeight: "bold" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {t("ambulance.loading")}
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: "red" }}>
                    {t("ambulance.failedToLoad")}
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {t("ambulance.noAmbulancesFound")}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((a, i) => (
                  <TableRow
                    key={a.id}
                    sx={{
                      backgroundColor: i % 2 === 0 ? "action.hover" : "background.default",
                      "&:last-child td": { border: 0 },
                    }}
                  >
                    <TableCell align="center">{i + 1}</TableCell>
                    <TableCell align="center">{a.ambulance_code}</TableCell>
                    <TableCell align="center">{a.plate_number}</TableCell>
                    <TableCell align="center">{a.vehicle_model}</TableCell>
                    <TableCell align="center">{a.assigned_driver_name || "—"}</TableCell>
                    <TableCell align="center">{a.driver_phone || "—"}</TableCell>
                    <TableCell align="center">{a.current_location || "—"}</TableCell>
                    <TableCell align="center">
                      <Chip label={statusLabel(a.status)} color={STATUS_COLOR[a.status] || "default"} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="info" onClick={() => setViewItem(a)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(a)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <DeleteMessageComponent />

      <Dialog open={formOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editItem ? t("ambulance.editAmbulance") : t("ambulance.addNewAmbulance")}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AmbulanceForm ambulance={editItem} onSuccess={handleClose} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("ambulance.ambulanceDetails")}
          <IconButton onClick={() => setViewItem(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewItem &&
            [
              [t("ambulance.ambulanceCode"), viewItem.ambulance_code],
              [t("ambulance.plateNumber"), viewItem.plate_number],
              [t("ambulance.vehicleModel"), viewItem.vehicle_model],
              [t("ambulance.assignedDriver"), viewItem.assigned_driver_name || "—"],
              [t("ambulance.driverPhone"), viewItem.driver_phone || "—"],
              [t("ambulance.currentLocation"), viewItem.current_location || "—"],
              [t("ambulance.fuelLevel"), viewItem.fuel_level ? `${viewItem.fuel_level}%` : "—"],
              [t("ambulance.odometer"), viewItem.odometer ? `${viewItem.odometer} KM` : "—"],
              [t("ambulance.lastServiceDate"), viewItem.last_service_date || "—"],
              [t("ambulance.equipmentStatus"), viewItem.equipment_status || "—"],
              [t("ambulance.status"), statusLabel(viewItem.status)],
              [t("ambulance.notes"), viewItem.notes || "—"],
            ].map(([label, val]) => (
              <Grid container key={label} sx={{ py: 0.75, borderBottom: "1px solid #f0f0f0" }}>
                <Grid item xs={5}>
                  <Typography fontWeight={600} variant="body2">
                    {label}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{val}</Typography>
                </Grid>
              </Grid>
            ))}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </Container>
  );
};

export default AmbulancePage;
